import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isPremium } from "@/lib/membership";
import { isVerified } from "@/lib/verification";
import { Calendar, MapPin, Users, Lock, Check, PartyPopper, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const FALLBACK = "https://images.unsplash.com/photo-1514525253161-7a46d19cd819";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function Events() {
  const { user } = useAuth();
  const verified = isVerified(user);
  const premium = isPremium(user) || user?.role === "admin";
  const [events, setEvents] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/events").then(({ data }) => setEvents(data)).catch(() => setEvents([]));
  useEffect(() => { load(); }, []);

  const rsvp = async (ev) => {
    setBusy(ev.id);
    try {
      const { data } = await api.post(`/events/${ev.id}/rsvp`);
      setEvents((list) => list.map((e) => e.id === ev.id ? { ...e, rsvped: data.rsvped, rsvp_count: data.rsvp_count } : e));
      toast.success(data.rsvped ? "You're on the list ✦" : "RSVP removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not RSVP");
    } finally {
      setBusy("");
    }
  };

  const locked = !verified || !premium;

  return (
    <div className="pt-24 pb-24 max-w-5xl mx-auto px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mb-12">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3 flex items-center gap-2"><PartyPopper size={14} /> Velluto Events</p>
        <h1 className="font-display text-6xl font-light">The members' calendar</h1>
        <p className="text-text-muted mt-4">Curated gatherings for our Premium circle. Reserve early — spaces are limited.</p>
      </motion.div>

      {locked && events?.length > 0 && (
        <div data-testid={!verified ? "events-verify-banner" : "events-premium-banner"}
          className="mb-8 rounded-3xl border border-gold/30 bg-gold/5 p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-5">
          <span className="w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center shrink-0">
            {!verified ? <ShieldCheck size={20} className="text-gold" /> : <Lock size={20} className="text-gold" />}
          </span>
          <div className="flex-1">
            <p className="font-display text-2xl font-light">{!verified ? "Verify to see event details" : "Group events are Premium"}</p>
            <p className="text-sm text-text-muted mt-1">
              {!verified
                ? "Locations, hosts and guest lists are reserved for identity-verified members. It takes about two minutes."
                : "Masquerades, munches and members-only soirées. Upgrade to Premium to see details and reserve your place."}
            </p>
          </div>
          {!verified
            ? <Link to="/verify" data-testid="events-verify-cta" className="shrink-0 px-7 py-3.5 rounded-full bg-gold text-ink text-xs uppercase tracking-[0.2em] hover:bg-gold/90 transition-colors text-center">Verify I'm 18+</Link>
            : <Link to="/premium" data-testid="events-upgrade" className="shrink-0 px-7 py-3.5 rounded-full bg-gold text-ink text-xs uppercase tracking-[0.2em] hover:bg-gold/90 transition-colors text-center">Unlock Premium</Link>}
        </div>
      )}

      {events === null ? (
        <p className="text-text-muted">Loading events…</p>
      ) : events.length === 0 ? (
        <div data-testid="events-empty" className="border border-white/10 rounded-3xl p-16 text-center bg-surface">
          <Calendar size={32} className="mx-auto text-text-muted mb-4" />
          <p className="font-display text-2xl font-light">No events on the calendar yet</p>
          <p className="text-text-muted mt-2 text-sm">Check back soon — new soirées are added regularly.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {events.map((ev, i) => (
            <motion.div key={ev.id} data-testid={`event-card-${ev.id}`}
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 * i }}
              className="rounded-3xl border border-white/10 bg-surface overflow-hidden flex flex-col">
              <div className="relative aspect-[16/9] overflow-hidden">
                <img src={ev.cover_url || FALLBACK} alt={ev.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
                <span className="absolute bottom-3 left-4 text-[11px] uppercase tracking-[0.15em] text-gold flex items-center gap-1.5"><Calendar size={12} /> {fmtDate(ev.starts_at)}</span>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="font-display text-3xl font-light leading-none">{ev.title}</h3>
                {ev.locked ? (
                  <div data-testid={`event-locked-${ev.id}`} className="mt-5 flex-1 flex flex-col">
                    <div className="space-y-2.5 select-none" aria-hidden>
                      <p className="flex items-center gap-1.5 text-xs text-text-muted"><MapPin size={12} /> <span className="blur-[5px]">Somewhere discreet, city centre</span></p>
                      <p className="text-sm text-text-main/60 blur-[5px]">An intimate evening for a curated circle of members. Dress code, host and guest list revealed once you're inside.</p>
                      <p className="flex items-center gap-1.5 text-xs text-text-muted"><Users size={13} /> <span className="blur-[5px]">18 going · hosted by Concierge</span></p>
                    </div>
                    <Link to={ev.locked_reason === "verify" ? "/verify" : "/premium"} data-testid={`event-unlock-${ev.id}`}
                      className="mt-6 flex items-center justify-center gap-2 w-full py-3 rounded-full border border-gold/40 text-gold text-xs uppercase tracking-[0.15em] hover:bg-gold/10 transition-colors">
                      {ev.locked_reason === "verify" ? <><ShieldCheck size={14} /> Verify to see details</> : <><Lock size={14} /> Unlock with Premium</>}
                    </Link>
                  </div>
                ) : (
                  <>
                    <p className="flex items-center gap-1.5 text-xs text-text-muted mt-3"><MapPin size={12} /> {ev.location}</p>
                    <p className="text-sm text-text-main/80 mt-4 flex-1">{ev.description}</p>
                    <div className="flex items-center justify-between mt-6">
                      <span className="flex items-center gap-1.5 text-xs text-text-muted"><Users size={13} /> {ev.rsvp_count} going{ev.capacity ? ` · ${ev.capacity} cap` : ""}</span>
                      <button data-testid={`event-rsvp-${ev.id}`} onClick={() => rsvp(ev)} disabled={busy === ev.id}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-xs uppercase tracking-[0.15em] transition-colors disabled:opacity-50 ${ev.rsvped ? "bg-gold text-ink hover:bg-gold/90" : "border border-white/15 hover:border-gold text-text-main"}`}>
                        {ev.rsvped ? <><Check size={14} /> Going</> : "RSVP"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
